import {
  ApiKey,
  ApiKeyList,
  AssociatedCountry,
  SearchResponse,
  UserPermission,
  UserRole,
  UsersData,
  Option,
  BasicOption,
} from "interfaces/dataInterfaces"
import { coreApi } from "./coreApi"

export const userApi = coreApi.injectEndpoints({
  endpoints: builder => ({
    getUser: builder.query<
      SearchResponse<UsersData>,
      { skip: number; take: number; adm0code?: number; regionData?: number; query?: string }
    >({
      query: ({ skip, take, adm0code, query, regionData }) => ({
        url: "Users/Get",
        method: "GET",
        params: { skip, top: take, adm0Code: adm0code, search: query || undefined, region: regionData },
      }),
      providesTags: [{ type: "Users" as const, id: "LIST" }],
    }),

    addUser: builder.mutation<void, Partial<UsersData>>({
      query: body => ({
        url: "Users/Post",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Users", id: "LIST" }],
    }),

    updateUser: builder.mutation<void, Partial<UsersData>>({
      query: body => ({
        url: "Users/Put",
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        { type: "Users", id: "LIST" },
        { type: "UserPermission" as const, id: "LIST" },
      ],
    }),

    deleteUser: builder.mutation<void, string>({
      query: id => ({
        url: `Users/Delete`,
        method: "DELETE",
        params: { id },
      }),
      invalidatesTags: [
        { type: "Users", id: "LIST" },
        { type: "UserPermission" as const, id: "LIST" },
      ],
    }),

    getRoles: builder.query<Array<UserRole>, void>({
      query: () => ({ url: "Users/GetRoles", method: "GET" }),
      providesTags: [{ type: "UsersRole" as const, id: "LIST" }],
    }),

    getModulesPermissions: builder.query<UserPermission[], { userID: string }>({
      query: ({ userID }) => ({
        url: "Users/GetModulesPermissions",
        method: "GET",
        params: { userId: userID },
      }),
      providesTags: () => [{ type: "UserPermission" as const, id: "LIST" }],
    }),

    associatedCountries: builder.query<Array<AssociatedCountry>, void>({
      query: () => ({ url: `Users/AssociatedCountries`, method: "GET" }),
      providesTags: [{ type: "AssociatedCountries" as const, id: "LIST" }],
    }),

    createApiKey: builder.mutation<void, ApiKey>({
      query: body => ({ url: `Users/CreateApiKey`, method: "POST", body }),
      invalidatesTags: [{ type: "ApiKeys" as const, id: "LIST" }],
    }),

    deleteApiKey: builder.mutation<void, number>({
      query: dataProviderKeyId => ({ url: `Users/DeleteApiKey/${dataProviderKeyId}`, method: "DELETE" }),
      invalidatesTags: [{ type: "ApiKeys" as const, id: "LIST" }],
    }),

    getApiKey: builder.query<ApiKeyList, number>({
      query: dataProviderKeyId => ({ url: `Users/GetApiKey/${dataProviderKeyId}`, method: "GET" }),
      providesTags: ["ApiKey"],
    }),

    editApiKey: builder.mutation<void, ApiKeyList>({
      query: body => ({ url: `Users/EditApiKey/${body.dataProviderKeyId}`, method: "PUT", body }),
      invalidatesTags: [{ type: "ApiKeys" as const, id: "LIST" }, "ApiKey"],
    }),

    getApiKeys: builder.query<SearchResponse<ApiKeyList>, { skip: number; take: number }>({
      query: ({ skip, take }) => ({
        url: `Users/GetApiKeys`,
        method: "GET",
        params: { skip, take },
      }),
      providesTags: [{ type: "ApiKeys" as const, id: "LIST" }],
    }),

    getVisibilityLevel: builder.query<{ visibility: string; email: string }, { userId: string }>({
      query: ({ userId }) => ({
        url: `Users/GetVisibilityLevel`,
        method: "GET",
        params: { userId },
      }),
    }),

    getDataProviderPlatforms: builder.query<Option[], void>({
      query: () => ({
        url: `Users/GetDataProviderPlatforms`,
      }),
      providesTags: [{ type: "ProviderPlatforms" as const, id: "LIST" }],
    }),

    getApiKeyName: builder.query<ApiKeyList, { dataProviderKeyId: number }>({
      query: ({ dataProviderKeyId }) => ({
        url: `Users/GetApikeyName/${dataProviderKeyId}`,
        method: "GET",
      }),
    }),

    checkEmail: builder.query<{ status: string }, { email: string }>({
      query: ({ email }) => ({
        url: `Users/GetUserStatus`,
        params: { email },
        method: "GET",
      }),
    }),

    getApiKeyList: builder.query<BasicOption[], { dataProviderPlatformId: number }>({
      query: ({ dataProviderPlatformId }) => ({
        url: `Users/GetApiKeysByPlatform`,
        params: { dataProviderPlatformId },
      }),
      providesTags: ["ApiKeyList"],
    }),
  }),
})

export const {
  useGetUserQuery,
  useAddUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useAssociatedCountriesQuery,
  useGetModulesPermissionsQuery,
  useCreateApiKeyMutation,
  useDeleteApiKeyMutation,
  useEditApiKeyMutation,
  useGetApiKeyQuery,
  useGetApiKeysQuery,
  useGetDataProviderPlatformsQuery,
  useGetVisibilityLevelQuery,
  useGetApiKeyNameQuery,
  useLazyCheckEmailQuery,
  useGetRolesQuery,
  useLazyGetRolesQuery,
  useGetApiKeyListQuery,
} = userApi
